/**
 * «Mejoras software» (20/10/2026).
 *
 * «Un menú "Mejoras software" al que solo accede una persona de
 * administración. Ahí escribe una mejora, pulsa "Aplicar" y se va guardando
 * un listado. Una rutina automática revisa lo pendiente, ejecuta cada mejora
 * y la marca como hecha.»
 *
 * Arriba se escribe la mejora, con sus adjuntos (27/10/2026: de cualquier
 * tipo; las imágenes se limpian como las fotos del chat y salen en galería, el
 * resto —PDF, Word, vídeo, zip…— se guarda tal cual y se descarga); abajo, el
 * listado con su estado, quién la escribió, cuándo, lo que contesta la rutina
 * y su historial.
 *
 * Aquí no se decide nada: entra quien tiene la casilla «Mejoras software» en
 * Usuarios (`SoloMejoras`), y lo que cada uno puede hacer con cada mejora
 * (editar, contestar, retirar, comentar, y a un admin dar el sí o descartar
 * lo que espera a dirección) lo dice el servidor en cada una
 * (services/mejoras_software.ficha). Los topes de los adjuntos (cuántos y
 * de cuánto) también los dice el servidor (`GET /api/mejoras-software`).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, formatDateTime, mensajeError } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { pareceUnaFoto } from '../lib/fotosChat';
import { tamanoLegible } from '../lib/pdfsChat';
import {
  Lightbulb, Paperclip, X, Loader2, Pencil, Undo2, MessageSquare, Send, History, Check,
  ShieldCheck, Ban, Download, File as FileIcon, FileText, FileSpreadsheet, FileVideo, FileAudio,
  FileArchive, FileImage, Presentation,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const RUTA = '/api/mejoras-software';

// Los topes de los adjuntos hasta que llegan los del servidor (son los mismos
// que services/mejoras_software.TOPE_ADJUNTOS y TOPE_BYTES_ADJUNTO).
export const TOPES_POR_DEFECTO = { tope_adjuntos: 8, tope_bytes_adjunto: 10 * 1024 * 1024 };

function topesDe(datos) {
  return {
    tope_adjuntos: Number(datos?.tope_adjuntos) || TOPES_POR_DEFECTO.tope_adjuntos,
    tope_bytes_adjunto: Number(datos?.tope_bytes_adjunto) || TOPES_POR_DEFECTO.tope_bytes_adjunto,
  };
}

function enMB(bytes) {
  return Math.round(bytes / (1024 * 1024));
}

// Los caracteres que cambian la dirección del texto: el servidor no los
// guarda en el nombre; aquí tampoco se pintan antes de subir.
const DIRECCION_RE = /[؜‎‏‪-‮⁦-⁩]/g;
function nombreVisible(nombre, porDefecto = 'adjunto') {
  return String(nombre || '').replace(DIRECCION_RE, '') || porDefecto;
}

/**
 * Qué es un archivo, para su icono y su etiqueta: por el tipo que dice el
 * servidor (o el navegador, antes de subirlo) y, si no dice nada, por la
 * extensión del nombre.
 */
export function claseDeArchivo(mime, nombre) {
  const tipo = String(mime || '').toLowerCase();
  const ext = (String(nombre || '').match(/\.([a-z0-9]{1,8})$/i) || ['', ''])[1].toLowerCase();
  if (tipo === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (tipo.includes('wordprocessingml') || tipo === 'application/msword' || /^(docx?|odt|rtf)$/.test(ext)) return 'word';
  if (tipo.includes('spreadsheetml') || tipo === 'application/vnd.ms-excel' || tipo === 'text/csv'
    || /^(xlsx?|ods|csv|numbers)$/.test(ext)) return 'excel';
  if (tipo.includes('presentationml') || tipo === 'application/vnd.ms-powerpoint' || /^(pptx?|odp|key)$/.test(ext)) return 'presentacion';
  if (tipo.startsWith('video/') || /^(mp4|mov|m4v|webm|mkv|avi)$/.test(ext)) return 'video';
  if (tipo.startsWith('audio/') || /^(mp3|m4a|wav|ogg|aac|flac|opus)$/.test(ext)) return 'audio';
  if (/zip|compressed|rar|7z|gzip|tar/.test(tipo) || /^(zip|rar|7z|gz|tgz|tar)$/.test(ext)) return 'zip';
  if (tipo.startsWith('image/') || /^(jpe?g|png|gif|webp|heic|heif|bmp|tiff?|svg)$/.test(ext)) return 'imagen';
  if (tipo.startsWith('text/') || tipo === 'application/json' || /^(txt|md|json|log|xml|html?)$/.test(ext)) return 'texto';
  return 'otro';
}

const ETIQUETA_CLASE = {
  pdf: 'PDF', word: 'Word', excel: 'Hoja de cálculo', presentacion: 'Presentación',
  video: 'Vídeo', audio: 'Audio', zip: 'Comprimido', imagen: 'Imagen', texto: 'Texto', otro: 'Archivo',
};
const ICONO_CLASE = {
  pdf: FileText, word: FileText, excel: FileSpreadsheet, presentacion: Presentation,
  video: FileVideo, audio: FileAudio, zip: FileArchive, imagen: FileImage, texto: FileText, otro: FileIcon,
};

/** El icono y la etiqueta de un archivo, por su tipo. */
function IconoArchivo({ clase, className = 'w-5 h-5' }) {
  const Icono = ICONO_CLASE[clase] || FileIcon;
  return <Icono className={className} aria-hidden="true" />;
}

// Los mismos nombres que el servidor (services/mejoras_software.ETIQUETAS):
// por si una ficha llega sin su etiqueta.
export const ETIQUETAS_ESTADO = {
  pendiente: 'Pendiente',
  en_curso: 'En curso',
  hecha: 'Hecha',
  necesita_aclaracion: 'Necesita aclaración',
  pendiente_direccion: 'Pendiente de dirección',
  descartada: 'Descartada',
};
const ORDEN_ESTADOS = Object.keys(ETIQUETAS_ESTADO);

// Pastillas suaves, con su par oscuro; solo lo que pide algo a quien la
// escribió (la aclaración) va en ámbar.
const CHIP_ESTADO = {
  pendiente: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100',
  en_curso: 'bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-100',
  hecha: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100',
  necesita_aclaracion: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100',
  pendiente_direccion: 'bg-violet-100 text-violet-900 dark:bg-violet-950 dark:text-violet-100',
  descartada: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300',
};

// Cómo se llama lo que contesta la rutina, según el estado.
const TITULO_RESPUESTA = {
  en_curso: 'Nota de la rutina',
  hecha: 'Resumen de lo hecho',
  necesita_aclaracion: 'La rutina pregunta',
  pendiente_direccion: 'Motivo',
};

function nuevoId() {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `m${Date.now()}${Math.random().toString(36).slice(2, 10)}`;
}

export function urlImagen(mejoraId, imagenId, { miniatura = false } = {}) {
  const base = `${BACKEND_URL}${RUTA}/${encodeURIComponent(mejoraId)}/imagenes/${encodeURIComponent(imagenId)}`;
  return miniatura ? `${base}/miniatura` : base;
}

/** La dirección de un adjunto que no es imagen: el servidor lo sirve siempre
 *  como descarga, con su nombre. Con sesión y con la casilla. */
export function urlArchivo(mejoraId, archivoId) {
  return `${BACKEND_URL}${RUTA}/${encodeURIComponent(mejoraId)}/archivos/${encodeURIComponent(archivoId)}`;
}

function ChipEstado({ estado, etiqueta }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${CHIP_ESTADO[estado] || CHIP_ESTADO.pendiente}`}
      data-testid="mejora-estado">
      {etiqueta || ETIQUETAS_ESTADO[estado] || estado}
    </span>
  );
}

/**
 * Los adjuntos elegidos y aún sin subir, de cualquier tipo: las imágenes con
 * su vista previa (`blob:`, que se suelta al quitarla o al salir) y el resto
 * con su nombre, tipo y tamaño. Los avisos de tope se dicen al elegir, con el
 * nombre del archivo (la regla de verdad la pone el servidor, mirando el
 * contenido; los topes vienen de él).
 */
function useAdjuntos(topes) {
  const [adjuntos, setAdjuntos] = useState([]);
  const vivas = useRef([]);
  useEffect(() => {
    const lista = vivas.current;
    return () => {
      lista.forEach((u) => { try { URL.revokeObjectURL(u); } catch { /* nada */ } });
    };
  }, []);

  const anadir = (archivos, yaHay = 0) => {
    const avisos = [];
    const nuevos = [];
    Array.from(archivos || []).forEach((archivo) => {
      if (!archivo) return;
      const nombre = archivo.name ? `«${nombreVisible(archivo.name)}»` : 'El archivo';
      if (yaHay + adjuntos.length + nuevos.length >= topes.tope_adjuntos) {
        avisos.push(`Como mucho ${topes.tope_adjuntos} adjuntos por mejora: ${nombre} no se ha añadido.`);
        return;
      }
      if (!archivo.size) { avisos.push(`${nombre} está vacío.`); return; }
      if (archivo.size > topes.tope_bytes_adjunto) {
        avisos.push(`${nombre} pesa ${tamanoLegible(archivo.size)} y el máximo son ${enMB(topes.tope_bytes_adjunto)} MB por archivo.`);
        return;
      }
      const esImagen = pareceUnaFoto(archivo);
      let url = '';
      if (esImagen) {
        try { url = URL.createObjectURL(archivo); vivas.current.push(url); } catch { url = ''; }
      }
      nuevos.push({ clave: nuevoId(), archivo, url, esImagen });
    });
    if (nuevos.length) setAdjuntos((c) => [...c, ...nuevos]);
    avisos.forEach((a) => toast.error(a));
  };
  const quitar = (clave) => setAdjuntos((c) => {
    const fuera = c.find((x) => x.clave === clave);
    if (fuera?.url) { try { URL.revokeObjectURL(fuera.url); } catch { /* nada */ } }
    return c.filter((x) => x.clave !== clave);
  });
  const vaciar = () => setAdjuntos((c) => {
    c.forEach((x) => { if (x.url) { try { URL.revokeObjectURL(x.url); } catch { /* nada */ } } });
    return [];
  });
  return { adjuntos, anadir, quitar, vaciar };
}

/** Un adjunto aún sin subir: miniatura si es imagen; si no, icono, nombre,
 *  tipo y tamaño. Con su botón de quitar. */
function PreviaAdjunto({ c, quitar, testid }) {
  const nombre = nombreVisible(c.archivo.name);
  const clase = c.esImagen ? 'imagen' : claseDeArchivo(c.archivo.type, c.archivo.name);
  const boton = (
    <button type="button" onClick={() => quitar(c.clave)}
      className="absolute top-0.5 right-0.5 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80"
      aria-label={`Quitar ${nombre}`} data-testid={`${testid}-quitar-previa`}>
      <X className="w-3 h-3" />
    </button>
  );
  if (c.esImagen && c.url) {
    return (
      <div className="relative h-20 w-20 rounded-md border border-border overflow-hidden bg-muted/40"
        title={nombre} data-testid={`${testid}-previa`} data-clase="imagen">
        <img src={c.url} alt="" className="h-full w-full object-cover" />
        {boton}
      </div>
    );
  }
  return (
    <div className="relative h-20 w-[200px] rounded-md border border-border bg-muted/40 flex items-center gap-2 pl-2 pr-7"
      title={nombre} data-testid={`${testid}-previa`} data-clase={clase}>
      <span className="shrink-0 inline-flex items-center justify-center w-9 h-11 rounded bg-[#2E6E5E]/10 text-[#2E6E5E] dark:text-emerald-200">
        <IconoArchivo clase={clase} />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-xs font-medium text-[#13231E] dark:text-foreground">{nombre}</span>
        <span className="block text-[11px] text-muted-foreground">
          {ETIQUETA_CLASE[clase]} · {tamanoLegible(c.archivo.size)}
        </span>
      </span>
      {boton}
    </div>
  );
}

function ElegirAdjuntos({ adjuntos, anadir, quitar, yaHay = 0, topes, testid }) {
  const input = useRef(null);
  const caben = yaHay + adjuntos.length < topes.tope_adjuntos;
  return (
    <div className="space-y-2">
      {adjuntos.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid={`${testid}-previas`}>
          {adjuntos.map((c) => <PreviaAdjunto key={c.clave} c={c} quitar={quitar} testid={testid} />)}
        </div>
      )}
      {/* Cualquier archivo: sin `accept`. Lo que es se decide en el servidor
          mirando el contenido. */}
      <input ref={input} type="file" multiple className="hidden"
        onChange={(e) => { anadir(e.target.files, yaHay); e.target.value = ''; }}
        data-testid={`${testid}-input`} />
      <Button type="button" variant="outline" size="sm" disabled={!caben}
        onClick={() => input.current && input.current.click()} data-testid={`${testid}-anadir`}>
        <Paperclip className="w-4 h-4 mr-2" />
        {caben ? 'Adjuntar archivos' : `Ya hay ${topes.tope_adjuntos} adjuntos`}
      </Button>
      <span className="ml-2 text-xs text-muted-foreground" data-testid={`${testid}-topes`}>
        Hasta {topes.tope_adjuntos}, de {enMB(topes.tope_bytes_adjunto)} MB cada uno: capturas, PDF, Word,
        Excel, vídeo, audio… Las imágenes se guardan sin los datos de la cámara.
      </span>
      {/* Lo lee la rutina, un asistente automático (revisión de seguridad,
          20/10/2026): que no viajen datos de pacientes por aquí. */}
      <p className="text-xs font-medium text-amber-800 dark:text-amber-200" role="note"
        data-testid={`${testid}-aviso-datos`}>
        No pongas datos de pacientes en el texto ni en los adjuntos: lo lee un asistente automático.
      </p>
    </div>
  );
}

function NuevaMejora({ onCreada, topes }) {
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const { adjuntos, anadir, quitar, vaciar } = useAdjuntos(topes);
  // El mismo id en cada reintento del MISMO envío: el servidor no la guarda
  // dos veces si la primera llegó y la respuesta no.
  const intento = useRef(null);

  const aplicar = async () => {
    const limpio = texto.trim();
    if (!limpio) { toast.error('Escribe la mejora: qué quieres que cambie y dónde.'); return; }
    if (!intento.current) intento.current = nuevoId();
    const datos = new FormData();
    datos.append('texto', limpio);
    datos.append('id', intento.current);
    adjuntos.forEach((c) => datos.append('adjuntos', c.archivo, c.archivo.name || 'adjunto'));
    setEnviando(true);
    try {
      const r = await api.post(RUTA, datos, { timeout: 180000 });
      intento.current = null;
      setTexto('');
      vaciar();
      toast.success('Guardada como «Pendiente». La rutina la revisará en su próxima vuelta.');
      onCreada && onCreada(r.data?.mejora);
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido guardar. Tu texto sigue aquí.'));
    } finally { setEnviando(false); }
  };

  return (
    <section className="rounded-xl border border-border bg-white dark:bg-card p-4 sm:p-5 mb-6" data-testid="mejora-nueva">
      <label htmlFor="mejora-texto" className="block font-semibold text-sm text-[#13231E] mb-1">
        ¿Qué quieres que cambie en el programa?
      </label>
      <p className="text-xs text-muted-foreground mb-3">
        Cuanto más concreto, mejor: en qué pantalla, qué pasa ahora y qué tendría que pasar.
        Una captura, un documento o un vídeo corto ayudan mucho.
      </p>
      <Textarea id="mejora-texto" value={texto} onChange={(e) => setTexto(e.target.value)}
        rows={8} maxLength={8000}
        placeholder="Ej.: En la Agenda, al dar una cita, quiero que el botón «Guardar» salga también arriba."
        className="min-h-[180px] text-sm" data-testid="mejora-texto" />
      <div className="mt-3 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <ElegirAdjuntos adjuntos={adjuntos} anadir={anadir} quitar={quitar} topes={topes}
          testid="mejora-capturas" />
        <Button onClick={aplicar} disabled={enviando}
          className="bg-[#2E6E5E] hover:bg-[#1E4A3F] shrink-0" data-testid="mejora-aplicar">
          {enviando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
          Aplicar
        </Button>
      </div>
    </section>
  );
}

function Historial({ eventos }) {
  if (!eventos?.length) return null;
  const linea = (e) => {
    const quien = e.conector ? `Rutina · «${e.conector}»` : (e.por_nombre || '—');
    let que;
    if (e.tipo === 'creada') que = 'La escribe: Pendiente';
    else if (e.tipo === 'edicion') que = `La edita${e.texto ? ` (${e.texto})` : ''}`;
    else if (e.tipo === 'aclaracion') que = 'Contesta la aclaración: vuelve a Pendiente';
    else if (e.tipo === 'comentario') que = 'Comenta';
    else if (e.tipo === 'nota') que = `Nota (${e.a_etiqueta})`;
    else if (e.tipo === 'aprobada') que = 'Da el sí de dirección: vuelve a Pendiente';
    else que = `${e.de_etiqueta || '—'} → ${e.a_etiqueta}`;
    return { quien, que };
  };
  return (
    <details className="mt-3 text-xs" data-testid="mejora-historial">
      <summary className="cursor-pointer text-muted-foreground inline-flex items-center gap-1 select-none">
        <History className="w-3.5 h-3.5" /> Historial ({eventos.length})
      </summary>
      <ol className="mt-2 space-y-1.5 border-l border-border pl-3">
        {eventos.map((e) => {
          const { quien, que } = linea(e);
          return (
            <li key={e.id} data-testid="mejora-evento">
              <span className="text-muted-foreground">{formatDateTime(e.at)}</span>
              {' · '}<span className="font-medium text-[#13231E] dark:text-foreground">{quien}</span>
              {' · '}{que}
              {e.version ? <span className="text-muted-foreground"> · versión {e.version}</span> : null}
              {e.nota ? <span className="text-muted-foreground"> · {e.nota}</span> : null}
              {e.texto && e.tipo !== 'edicion' ? (
                <div className="whitespace-pre-wrap text-[#13231E] dark:text-foreground mt-0.5">{e.texto}</div>
              ) : null}
            </li>
          );
        })}
      </ol>
    </details>
  );
}

function Editar({ m, onHecho, onCancelar, topes }) {
  const [texto, setTexto] = useState(m.texto);
  const [quitar, setQuitar] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const archivos = m.archivos || [];
  const quedan = (m.imagenes || []).filter((i) => !quitar.includes(i.id)).length
    + archivos.filter((a) => !quitar.includes(a.id)).length;
  const { adjuntos, anadir, quitar: quitarPrevia, vaciar } = useAdjuntos(topes);
  const alternar = (id) => setQuitar((q) => (q.includes(id) ? q.filter((x) => x !== id) : [...q, id]));

  const guardar = async () => {
    if (!texto.trim()) { toast.error('La mejora no puede quedarse sin texto.'); return; }
    const datos = new FormData();
    datos.append('texto', texto.trim());
    datos.append('revision', String(m.revision ?? ''));
    quitar.forEach((id) => datos.append('quitar', id));
    adjuntos.forEach((c) => datos.append('adjuntos', c.archivo, c.archivo.name || 'adjunto'));
    setGuardando(true);
    try {
      const r = await api.put(`${RUTA}/${m.id}`, datos, { timeout: 180000 });
      vaciar();
      toast.success('Mejora corregida');
      onHecho(r.data?.mejora);
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido guardar'));
    } finally { setGuardando(false); }
  };

  return (
    <div className="space-y-3" data-testid="mejora-editar">
      <Textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={6} maxLength={8000}
        className="text-sm" data-testid="mejora-editar-texto" />
      {((m.imagenes || []).length > 0 || archivos.length > 0) && (
        <div className="flex flex-wrap gap-2">
          {(m.imagenes || []).map((i) => {
            const fuera = quitar.includes(i.id);
            return (
              <button type="button" key={i.id} onClick={() => alternar(i.id)}
                className={`relative h-20 w-20 rounded-md border overflow-hidden ${fuera ? 'opacity-40 border-red-400' : 'border-border'}`}
                title={fuera ? 'Se quitará al guardar (pulsa para dejarla)' : 'Pulsa para quitarla'}
                data-testid="mejora-editar-imagen">
                <img src={urlImagen(m.id, i.id, { miniatura: true })} alt="" className="h-full w-full object-cover" />
                {fuera && <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-red-800 bg-white/60">Se quita</span>}
              </button>
            );
          })}
          {archivos.map((a) => {
            const fuera = quitar.includes(a.id);
            const clase = claseDeArchivo(a.mime, a.nombre);
            return (
              <button type="button" key={a.id} onClick={() => alternar(a.id)}
                className={`relative h-20 w-[200px] rounded-md border bg-muted/40 flex items-center gap-2 px-2 text-left ${fuera ? 'opacity-40 border-red-400' : 'border-border'}`}
                title={fuera ? 'Se quitará al guardar (pulsa para dejarlo)' : 'Pulsa para quitarlo'}
                data-testid="mejora-editar-archivo">
                <span className="shrink-0 inline-flex items-center justify-center w-9 h-11 rounded bg-[#2E6E5E]/10 text-[#2E6E5E] dark:text-emerald-200">
                  <IconoArchivo clase={clase} />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-xs font-medium text-[#13231E] dark:text-foreground">{a.nombre}</span>
                  <span className="block text-[11px] text-muted-foreground">{ETIQUETA_CLASE[clase]} · {tamanoLegible(a.tamano)}</span>
                </span>
                {fuera && <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-red-800 bg-white/60">Se quita</span>}
              </button>
            );
          })}
        </div>
      )}
      <ElegirAdjuntos adjuntos={adjuntos} anadir={anadir} quitar={quitarPrevia} yaHay={quedan}
        topes={topes} testid="mejora-editar-capturas" />
      <div className="flex gap-2">
        <Button size="sm" onClick={guardar} disabled={guardando}
          className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="mejora-editar-guardar">
          {guardando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
          Guardar
        </Button>
        <Button size="sm" variant="outline" onClick={() => { vaciar(); onCancelar(); }}
          data-testid="mejora-editar-cancelar">Cancelar</Button>
      </div>
    </div>
  );
}

/** Un recuadro de texto con su botón: contestar la aclaración o comentar. */
function Escribir({ etiqueta, placeholder, boton, ruta, testid, onHecho, aviso }) {
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const enviar = async () => {
    if (!texto.trim()) { toast.error('Escribe algo antes de enviarlo.'); return; }
    setEnviando(true);
    try {
      const r = await api.post(ruta, { texto: texto.trim() });
      setTexto('');
      if (aviso) toast.success(aviso);
      onHecho(r.data?.mejora);
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido enviar'));
    } finally { setEnviando(false); }
  };
  return (
    <div className="space-y-2" data-testid={testid}>
      <label className="block text-xs font-medium text-[#13231E] dark:text-foreground">{etiqueta}</label>
      <Textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={3}
        placeholder={placeholder} className="text-sm" data-testid={`${testid}-texto`} />
      <Button size="sm" onClick={enviar} disabled={enviando} variant="outline"
        data-testid={`${testid}-enviar`}>
        {enviando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
        {boton}
      </Button>
    </div>
  );
}

/** Los adjuntos que no son imágenes, en la mejora: icono por tipo, nombre,
 *  tipo y tamaño, y el enlace de descarga (el servidor los sirve siempre como
 *  descarga, nunca inline). */
function ListaArchivos({ mejoraId, archivos }) {
  if (!archivos?.length) return null;
  return (
    <ul className="mt-3 space-y-1.5" data-testid="mejora-archivos">
      {archivos.map((a) => {
        const clase = claseDeArchivo(a.mime, a.nombre);
        return (
          <li key={a.id} className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2 py-1.5 text-sm"
            data-testid="mejora-archivo" data-clase={clase}>
            <span className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded bg-[#2E6E5E]/10 text-[#2E6E5E] dark:text-emerald-200">
              <IconoArchivo clase={clase} className="w-4 h-4" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-[#13231E] dark:text-foreground" data-testid="mejora-archivo-nombre">{a.nombre}</span>
              <span className="block text-[11px] text-muted-foreground">{ETIQUETA_CLASE[clase]} · {tamanoLegible(a.tamano)}</span>
            </span>
            <a href={urlArchivo(mejoraId, a.id)} download={a.nombre}
              className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-[#2E6E5E] hover:underline dark:text-emerald-200"
              title={`Descargar ${a.nombre}`} data-testid="mejora-archivo-descargar">
              <Download className="w-3.5 h-3.5" aria-hidden="true" /> Descargar
            </a>
          </li>
        );
      })}
    </ul>
  );
}

function TarjetaMejora({ m, onCambio, topes }) {
  const [editando, setEditando] = useState(false);
  const [comentando, setComentando] = useState(false);
  const [retirando, setRetirando] = useState(false);
  const [decidiendo, setDecidiendo] = useState(false);
  const titulo = TITULO_RESPUESTA[m.estado];

  // Lo que espera a dirección (20/10/2026): el sí, o descartarla. Solo se le
  // ofrece a quien el servidor dice (`puedo_aprobar`, `puedo_descartar`).
  const decidir = async (que) => {
    const pregunta = que === 'aprobar'
      ? '¿Dar el sí a esta mejora? Vuelve a «Pendiente» y la rutina la hará y la desplegará.'
      : '¿Descartar esta mejora? Quedará como «Descartada» y la rutina no la hará.';
    if (!window.confirm(pregunta)) return;
    setDecidiendo(true);
    try {
      const r = await api.post(`${RUTA}/${m.id}/${que}`);
      toast.success(que === 'aprobar' ? 'Sí dado: vuelve a «Pendiente»' : 'Mejora descartada');
      onCambio(r.data?.mejora);
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido cambiar'));
    } finally { setDecidiendo(false); }
  };

  const retirar = async () => {
    if (!window.confirm('¿Retirar esta mejora? Quedará como «Descartada» y la rutina no la hará.')) return;
    setRetirando(true);
    try {
      const r = await api.post(`${RUTA}/${m.id}/retirar`);
      toast.success('Mejora retirada');
      onCambio(r.data?.mejora);
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido retirar'));
    } finally { setRetirando(false); }
  };

  return (
    <li className={`rounded-xl border border-border bg-white dark:bg-card p-4 ${m.estado === 'descartada' ? 'opacity-70' : ''}`}
      data-testid="mejora-item" data-estado={m.estado}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground mb-2">
        <ChipEstado estado={m.estado} etiqueta={m.estado_etiqueta} />
        <span>{formatDateTime(m.creado_at)}</span>
        <span>·</span>
        <span data-testid="mejora-autor">{m.autor_nombre || '—'}</span>
        {m.aprobada ? (
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 bg-emerald-50 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100"
            title={`Sí de dirección: ${m.aprobada.por_nombre || '—'}, ${formatDateTime(m.aprobada.at)}`}
            data-testid="mejora-aprobada">
            <ShieldCheck className="w-3 h-3" /> Con el sí de dirección ({m.aprobada.por_nombre || '—'})
          </span>
        ) : null}
        {m.estado === 'hecha' && m.version ? (
          <span className="ml-auto font-mono text-[11px]" data-testid="mejora-version">versión {m.version}</span>
        ) : null}
      </div>

      {editando ? (
        <Editar m={m} topes={topes} onCancelar={() => setEditando(false)}
          onHecho={(nueva) => { setEditando(false); onCambio(nueva); }} />
      ) : (
        <>
          <p className="whitespace-pre-wrap text-sm text-[#13231E] dark:text-foreground" data-testid="mejora-texto-guardado">{m.texto}</p>
          {(m.imagenes || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {m.imagenes.map((i) => (
                <a key={i.id} href={urlImagen(m.id, i.id)} target="_blank" rel="noreferrer"
                  className="block h-20 w-20 rounded-md border border-border overflow-hidden hover:ring-2 hover:ring-[#2E6E5E]/40"
                  title="Abrir la captura" data-testid="mejora-imagen">
                  <img src={urlImagen(m.id, i.id, { miniatura: true })} alt="Captura de la mejora"
                    className="h-full w-full object-cover" loading="lazy" />
                </a>
              ))}
            </div>
          )}
          <ListaArchivos mejoraId={m.id} archivos={m.archivos} />
        </>
      )}

      {titulo && m.respuesta ? (
        <div className={`mt-3 rounded-lg p-3 text-sm ${m.estado === 'necesita_aclaracion'
          ? 'bg-amber-50 text-amber-950 dark:bg-amber-950 dark:text-amber-50'
          : 'bg-muted/50 text-[#13231E] dark:text-foreground'}`} data-testid="mejora-respuesta">
          <div className="text-xs font-semibold mb-1">{titulo}</div>
          <div className="whitespace-pre-wrap">{m.respuesta}</div>
        </div>
      ) : null}

      {(m.puedo_aprobar || m.puedo_descartar) && !editando ? (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="mejora-decision">
          {m.puedo_aprobar && (
            <Button size="sm" onClick={() => decidir('aprobar')} disabled={decidiendo}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="mejora-aprobar">
              <ShieldCheck className="w-4 h-4 mr-2" /> Dar el sí
            </Button>
          )}
          {m.puedo_descartar && (
            <Button size="sm" variant="outline" onClick={() => decidir('descartar')} disabled={decidiendo}
              data-testid="mejora-descartar">
              <Ban className="w-4 h-4 mr-2" /> Descartar
            </Button>
          )}
        </div>
      ) : null}

      {m.puedo_contestar && !editando ? (
        <div className="mt-3">
          <Escribir etiqueta="Tu respuesta (la mejora vuelve a «Pendiente»)"
            placeholder="Contesta a la pregunta de la rutina…" boton="Enviar respuesta"
            ruta={`${RUTA}/${m.id}/aclaracion`} testid="mejora-contestar"
            aviso="Contestada: vuelve a «Pendiente»" onHecho={onCambio} />
        </div>
      ) : null}

      {!editando && (m.puedo_editar || m.puedo_retirar || m.puedo_comentar) ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {m.puedo_editar && (
            <Button size="sm" variant="outline" onClick={() => setEditando(true)} data-testid="mejora-editar-abrir">
              <Pencil className="w-4 h-4 mr-2" /> Editar
            </Button>
          )}
          {m.puedo_retirar && (
            <Button size="sm" variant="outline" onClick={retirar} disabled={retirando} data-testid="mejora-retirar">
              <Undo2 className="w-4 h-4 mr-2" /> Retirar
            </Button>
          )}
          {m.puedo_comentar && !comentando && (
            <Button size="sm" variant="ghost" onClick={() => setComentando(true)} data-testid="mejora-comentar-abrir">
              <MessageSquare className="w-4 h-4 mr-2" /> Comentar
            </Button>
          )}
        </div>
      ) : null}

      {comentando && m.puedo_comentar && !editando ? (
        <div className="mt-3">
          <Escribir etiqueta="Comentario" placeholder="Algo más que deba saber la rutina o el equipo…"
            boton="Añadir comentario" ruta={`${RUTA}/${m.id}/comentarios`} testid="mejora-comentar"
            aviso="Comentario añadido" onHecho={(nueva) => { setComentando(false); onCambio(nueva); }} />
        </div>
      ) : null}

      <Historial eventos={m.historial} />
    </li>
  );
}

export default function MejorasSoftwarePage() {
  const [mejoras, setMejoras] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtro, setFiltro] = useState('');
  // Cuántos adjuntos y de cuánto: lo dice el servidor con el listado.
  const [topes, setTopes] = useState(TOPES_POR_DEFECTO);

  const cargar = useCallback(async () => {
    try {
      const r = await api.get(RUTA);
      setMejoras(r.data?.mejoras || []);
      setTopes(topesDe(r.data));
    } catch (e) {
      toast.error(mensajeError(e, 'No se han podido leer las mejoras'));
    } finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Lo que devuelve el servidor tras un cambio sustituye a la que había; si
  // no viene, se vuelve a leer la lista.
  const alCambiar = (nueva) => {
    if (!nueva?.id) { cargar(); return; }
    setMejoras((lista) => {
      const hay = lista.some((x) => x.id === nueva.id);
      return hay ? lista.map((x) => (x.id === nueva.id ? nueva : x)) : [nueva, ...lista];
    });
  };

  const cuentas = mejoras.reduce((acc, m) => ({ ...acc, [m.estado]: (acc[m.estado] || 0) + 1 }), {});
  const visibles = filtro ? mejoras.filter((m) => m.estado === filtro) : mejoras;

  return (
    <div className="max-w-4xl mx-auto" data-testid="mejoras-software-page">
      <PageHeader title="Mejoras software"
        subtitle="Escribe aquí lo que quieres que cambie en el programa. Una rutina automática las revisa cada pocas horas, las hace y te contesta aquí. Lo delicado (dinero, permisos, documentos legales o borrar datos) espera al sí de dirección." />

      <NuevaMejora onCreada={alCambiar} topes={topes} />

      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="font-display font-semibold text-lg text-[#13231E] dark:text-foreground">Listado</h2>
        <label className="text-xs text-muted-foreground flex items-center gap-2">
          Ver
          <select value={filtro} onChange={(e) => setFiltro(e.target.value)}
            className="h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground"
            data-testid="mejoras-filtro">
            <option value="">Todas ({mejoras.length})</option>
            {ORDEN_ESTADOS.map((e) => (
              <option key={e} value={e}>{ETIQUETAS_ESTADO[e]} ({cuentas[e] || 0})</option>
            ))}
          </select>
        </label>
      </div>

      {cargando ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Cargando…</div>
      ) : visibles.length === 0 ? (
        <EmptyState icon={Lightbulb}
          title={filtro ? 'Ninguna mejora en este estado' : 'Todavía no hay ninguna mejora'}
          description={filtro ? 'Cambia el filtro para ver las demás.' : 'Escribe la primera arriba y pulsa «Aplicar».'} />
      ) : (
        <ul className="space-y-3" data-testid="mejoras-lista">
          {visibles.map((m) => <TarjetaMejora key={m.id} m={m} onCambio={alCambiar} topes={topes} />)}
        </ul>
      )}
    </div>
  );
}
